import {
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactElement,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Ban,
  Check as CheckIcon,
  Clock,
  Info,
  Loader2,
  RotateCw,
  TimerOff,
  X,
  type LucideIcon,
} from "lucide-react";
import { humanizeError } from "../lib/errors.ts";

/**
 * Nocturne tasarım sisteminin React karşılıkları.
 *
 * Sınıflar `index.css` içinde tanımlı (.card, .btn, .row, .chip …); buradaki
 * bileşenler o sınıfları ve tekrar eden düzeni sarmalıyor. Yeni sayfa kodu
 * keyfi `text-[11.5px]` yerine ölçekteki basamakları kullanır (text-2xs … text-xl).
 */

export type Tone = "ok" | "warn" | "danger" | "idle" | "accent";

export const toneColor: Record<Tone, string> = {
  ok: "var(--color-ok)",
  warn: "var(--color-warn)",
  danger: "var(--color-danger)",
  idle: "var(--color-neutral-600)",
  accent: "var(--color-accent-400)",
};

export function Card({
  title,
  icon: Icon,
  hint,
  action,
  children,
  flush = false,
  className = "",
  style,
  id,
}: {
  title?: ReactNode;
  icon?: LucideIcon;
  /** Başlığın yanındaki açıklama — sönük ve küçük. */
  hint?: ReactNode;
  action?: ReactNode;
  children?: ReactNode;
  /** true: içerik kendi dolgusunu yönetir (liste/tablo kartları). */
  flush?: boolean;
  className?: string;
  style?: React.CSSProperties;
  id?: string;
}): ReactNode {
  return (
    <section id={id} className={`card ${className}`} style={style}>
      {(title || action) && (
        <header className="card-head">
          <h2>
            {Icon && <Icon size={15} strokeWidth={1.75} className="text-neutral-500" />}
            {title}
          </h2>
          {hint && <span className="text-2xs text-neutral-600">{hint}</span>}
          {action && <div className="ml-auto flex flex-wrap items-center gap-2">{action}</div>}
        </header>
      )}
      {children && <div className={flush ? "" : "p-4"}>{children}</div>}
    </section>
  );
}

export function Dot({ tone, glow = false }: { tone: Tone; glow?: boolean }): ReactNode {
  return (
    <span
      aria-hidden
      className="inline-block size-1.5 shrink-0 rounded-full"
      style={{
        background: toneColor[tone],
        ...(glow ? { boxShadow: `0 0 8px ${toneColor[tone]}` } : {}),
      }}
    />
  );
}

type Variant = "primary" | "secondary" | "ghost" | "danger";

export function Button({
  variant = "secondary",
  size = "md",
  icon: Icon,
  loading = false,
  className = "",
  children,
  disabled,
  type = "button",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: "sm" | "md";
  icon?: LucideIcon;
  /** true: dönen gösterge + tıklanamaz; çift tıklamayla çift iş başlamasın. */
  loading?: boolean;
}): ReactNode {
  // Yüklenirken `disabled` DEĞİL: disabled düğme odağı kaybettirir, klavye
  // kullanıcısı sayfanın başına düşer. aria-disabled + tıklamayı yut.
  const { onClick, ...rest } = props;
  return (
    <button
      {...rest}
      type={type}
      disabled={disabled}
      aria-disabled={loading || undefined}
      aria-busy={loading || undefined}
      onClick={(e) => {
        if (loading) {
          e.preventDefault();
          return;
        }
        onClick?.(e);
      }}
      className={`btn btn-${variant} ${size === "sm" ? "btn-sm" : ""} ${className}`}
    >
      {loading ? (
        <Loader2 size={14} className="spin" aria-hidden />
      ) : (
        Icon && <Icon size={14} strokeWidth={1.9} aria-hidden />
      )}
      {children}
    </button>
  );
}

/**
 * Geri alınamaz işler için iki tıklamalı onay: ilk tık "Emin misin?"e döner,
 * 4 sn içinde ikinci tık çalıştırır. Pencere açmadan, bulunduğu yerde.
 */
export function ConfirmButton({
  onConfirm,
  confirmLabel = "Emin misin?",
  children,
  variant = "danger",
  ...props
}: Omit<Parameters<typeof Button>[0], "onClick"> & {
  onConfirm: () => void | Promise<void>;
  confirmLabel?: string;
}): ReactNode {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <Button
      {...props}
      variant={armed ? "danger" : variant}
      onClick={() => {
        if (!armed) return setArmed(true);
        setArmed(false);
        void onConfirm();
      }}
    >
      {armed ? confirmLabel : children}
    </Button>
  );
}

const STATUS: Record<string, { label: string; tone: Tone; icon: LucideIcon; spin?: boolean }> = {
  queued: { label: "kuyrukta", tone: "idle", icon: Clock },
  claimed: { label: "başlıyor", tone: "accent", icon: Loader2, spin: true },
  running: { label: "çalışıyor", tone: "accent", icon: Loader2, spin: true },
  succeeded: { label: "başarılı", tone: "ok", icon: CheckIcon },
  failed: { label: "başarısız", tone: "danger", icon: X },
  canceled: { label: "iptal", tone: "idle", icon: Ban },
  expired: { label: "süresi doldu", tone: "idle", icon: TimerOff },
};

export function statusLabel(status: string): string {
  return STATUS[status]?.label ?? status;
}

/**
 * Tonlu rozet: renk + ikon + metin birlikte (durum yalnızca renkle anlatılmaz).
 * `title` yalnızca ek ayrıntı içindir; asıl anlam her zaman görünen metinde.
 */
export function TonePill({
  tone,
  icon: Icon,
  children,
  title,
  spin = false,
}: {
  tone: Tone;
  icon?: LucideIcon;
  children: ReactNode;
  title?: string;
  spin?: boolean;
}): ReactNode {
  const color = toneColor[tone];
  return (
    <span
      title={title}
      className="inline-flex items-center gap-1 rounded-full px-2 py-[2px] text-2xs font-medium whitespace-nowrap"
      style={{ color, background: `color-mix(in srgb, ${color} 13%, transparent)` }}
    >
      {Icon && <Icon size={12} strokeWidth={2.2} className={spin ? "spin" : ""} aria-hidden />}
      {children}
    </span>
  );
}

/** Durum rozeti: renk + ikon + metin (yalnızca renge dayanmaz). */
export function StatusPill({ status }: { status: string }): ReactNode {
  const s = STATUS[status] ?? { label: status, tone: "idle" as Tone, icon: Info };
  return (
    <TonePill tone={s.tone} icon={s.icon} spin={s.spin}>
      {s.label}
    </TonePill>
  );
}

export function statusTone(status: string): Tone {
  return STATUS[status]?.tone ?? "idle";
}

/** Çalışan işlerdeki ilerleme çubuğu (yüzde yoksa belirsiz). */
export function ProgressBar({ pct }: { pct?: number | null }): ReactNode {
  return (
    <div className="h-[2px] overflow-hidden rounded-[2px] bg-neutral-900">
      <div
        className={`h-[2px] bg-accent ${pct == null ? "indeterminate w-[30%]" : ""}`}
        style={pct == null ? undefined : { width: `${Math.min(100, Math.max(0, pct))}%` }}
      />
    </div>
  );
}

/** Panolardaki sayı kutusu. `to` verilirse tıklanabilir bağlantıdır. */
export function Tile({
  label,
  value,
  note,
  tone = "accent",
  to,
  icon: Icon,
}: {
  label: string;
  value: ReactNode;
  note?: ReactNode;
  tone?: Tone;
  to?: string;
  icon?: LucideIcon;
}): ReactNode {
  const body = (
    <>
      <span className="kicker flex items-center gap-1.5">
        {Icon && <Icon size={13} strokeWidth={1.9} aria-hidden />}
        {label}
      </span>
      <span
        className="text-xl leading-none font-semibold tracking-[-0.02em] tabular-nums"
        style={{ color: toneColor[tone] }}
      >
        {value}
      </span>
      {note && <span className="text-xs text-neutral-500">{note}</span>}
    </>
  );
  const cls = "card grid content-start gap-2 px-4 py-3.5";
  return to ? (
    <Link to={to} className={`${cls} transition hover:border-neutral-800 hover:bg-surface-2`}>
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  );
}

/**
 * Form alanı. Etiket, tek bir input/textarea/select çocuğuna `id` ile bağlanır
 * (ekran okuyucu ve etikete tıklayınca odak). Chips gibi grup çocuklarında
 * etiket grubun adı olur.
 */
export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
}): ReactNode {
  const id = useId();
  const single =
    isValidElement(children) &&
    typeof children.type === "string" &&
    ["input", "textarea", "select"].includes(children.type);
  const child = single
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
        id: (children.props as { id?: string }).id ?? id,
        "aria-invalid": error ? true : undefined,
        "aria-describedby": hint || error ? `${id}-hint` : undefined,
      })
    : children;
  return (
    <div className="grid gap-1.5" role={single ? undefined : "group"} aria-labelledby={single ? undefined : `${id}-label`}>
      {single ? (
        <label htmlFor={(children.props as { id?: string }).id ?? id} className="label mb-0">
          {label}
        </label>
      ) : (
        <span id={`${id}-label`} className="label mb-0">
          {label}
        </span>
      )}
      {child}
      {(error || hint) && (
        <span id={`${id}-hint`} className={`text-2xs ${error ? "text-danger" : "text-neutral-600"}`}>
          {error ?? hint}
        </span>
      )}
    </div>
  );
}

/** Tek seçimli düğme grubu (klavye ve ekran okuyucuyla kullanılabilir). */
export function Chips<T extends string>({
  values,
  value,
  onChange,
  labels,
  "aria-label": ariaLabel,
}: {
  values: readonly T[];
  value: T | string;
  onChange: (v: T) => void;
  /** Görünen ad; gönderilen değer `values`tır. */
  labels?: Partial<Record<T, ReactNode>>;
  /** Grubun ekran okuyucu adı, ör. "Filtre". */
  "aria-label"?: string;
}): ReactNode {
  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label={ariaLabel}>
      {values.map((v) => (
        <button
          key={v}
          type="button"
          className="chip inline-flex items-center gap-1"
          aria-pressed={v === value}
          onClick={() => onChange(v)}
        >
          {/* Seçili olan yalnızca renkle değil işaretle de belli olsun. */}
          {v === value && <CheckIcon size={12} strokeWidth={2.4} aria-hidden />}
          {labels?.[v] ?? v}
        </button>
      ))}
    </div>
  );
}

export function Check({
  label,
  checked,
  onChange,
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (v: boolean) => void;
}): ReactNode {
  return (
    <label className="flex cursor-pointer items-start gap-2 text-xs text-neutral-300">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-[2px] size-3.5 shrink-0 accent-[var(--color-accent)]"
      />
      <span>{label}</span>
    </label>
  );
}

export function Empty({ children }: { children: ReactNode }): ReactNode {
  return (
    <p role="status" className="px-4 py-8 text-center text-xs text-neutral-600">
      {children}
    </p>
  );
}

/**
 * Veri alınamadığında. "Boş" ile karıştırılmasın: hata varken "kayıt yok" ya da
 * yeşil "hepsi açık" göstermek yanlış güven verir.
 */
export function ErrorState({
  error,
  onRetry,
  compact = false,
}: {
  error: unknown;
  onRetry?: () => void;
  compact?: boolean;
}): ReactNode {
  return (
    <div
      role="alert"
      className={`flex flex-wrap items-center gap-3 text-xs text-neutral-300 ${compact ? "px-4 py-3" : "px-4 py-6"}`}
    >
      <AlertTriangle size={15} className="shrink-0 text-danger" aria-hidden />
      <span className="min-w-0 flex-1">{humanizeError(error)}</span>
      {onRetry && (
        <Button size="sm" icon={RotateCw} onClick={onRetry}>
          Tekrar dene
        </Button>
      )}
    </div>
  );
}

/** Uyarı şeridi. */
export function Banner({
  tone = "warn",
  kicker,
  children,
  action,
}: {
  tone?: "warn" | "danger" | "info";
  kicker?: string;
  children: ReactNode;
  action?: ReactNode;
}): ReactNode {
  const color =
    tone === "warn" ? "var(--color-warn)" : tone === "danger" ? "var(--color-danger)" : "var(--color-accent-400)";
  const Icon = tone === "info" ? Info : AlertTriangle;
  return (
    <div
      role={tone === "danger" ? "alert" : "status"}
      className="flex flex-wrap items-center gap-3 rounded-[var(--radius-md)] border px-3.5 py-2.5"
      style={{
        borderColor: `color-mix(in srgb, ${color} 35%, transparent)`,
        background: `color-mix(in srgb, ${color} 7%, transparent)`,
      }}
    >
      <Icon size={15} style={{ color }} className="shrink-0" aria-hidden />
      {kicker && (
        <span className="text-2xs font-semibold tracking-[0.08em] uppercase" style={{ color }}>
          {kicker}
        </span>
      )}
      {/* basis: dar ekranda metin etiketin yanında sıkışmak yerine alt satıra iner. */}
      <span className="min-w-0 flex-1 basis-[15rem] text-sm text-neutral-200">{children}</span>
      {action && <div className="flex items-center gap-2">{action}</div>}
    </div>
  );
}

/**
 * Tıklanabilir liste satırı: gezinmeyse bağlantı, eylemse düğme. div+onClick
 * klavyeyle ulaşılamıyordu.
 */
export function RowLink({
  to,
  onClick,
  children,
  className = "",
}: {
  to?: string;
  onClick?: () => void;
  children: ReactNode;
  className?: string;
}): ReactNode {
  const cls = `row row-hover ${className}`;
  return to ? (
    <Link to={to} className={cls}>
      {children}
    </Link>
  ) : (
    <button type="button" onClick={onClick} className={cls}>
      {children}
    </button>
  );
}

/**
 * Erişilebilir pencere: Esc ve arka plana tıklama kapatır, açılınca ilk alana
 * odaklanır, kapanınca odağı geri verir.
 */
export function Modal({
  title,
  subtitle,
  onClose,
  children,
  footer,
  width = 520,
  dismissable = true,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
  /** false: iş sürerken yanlışlıkla kapanmasın. */
  dismissable?: boolean;
}): ReactNode {
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const first = panel.current?.querySelector<HTMLElement>(
      "[autofocus], input, textarea, select, button:not([disabled])",
    );
    first?.focus();
    return () => previous?.focus?.();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && dismissable) onClose();
      if (e.key !== "Tab" || !panel.current) return;
      // Basit odak kapanı: Tab pencerenin dışına çıkmasın.
      const nodes = panel.current.querySelectorAll<HTMLElement>(
        "a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex='-1'])",
      );
      if (!nodes.length) return;
      const first = nodes[0]!;
      const last = nodes[nodes.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, dismissable]);

  return (
    <div
      className="fixed inset-0 z-40 grid place-items-center overflow-y-auto p-4 sm:p-6"
      style={{ background: "rgba(8,9,15,0.72)", backdropFilter: "blur(3px)" }}
      onMouseDown={(e) => {
        if (dismissable && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={subtitle ? `${titleId}-sub` : undefined}
        className="card animate-modal-in flex max-h-[calc(100dvh-2rem)] w-full flex-col p-0 sm:max-h-[calc(100dvh-3rem)]"
        style={{ maxWidth: width, boxShadow: "var(--shadow-lg)" }}
      >
        <div className="flex shrink-0 items-start gap-3 border-b border-neutral-900 px-4 py-3.5">
          <div className="grid min-w-0 gap-0.5">
            <h2 id={titleId} className="text-md font-semibold">
              {title}
            </h2>
            {subtitle && (
              <div id={`${titleId}-sub`} className="text-xs text-neutral-500">
                {subtitle}
              </div>
            )}
          </div>
          {dismissable && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Kapat"
              className="ml-auto grid size-7 shrink-0 cursor-pointer place-items-center rounded-[var(--radius-sm)] text-neutral-500 hover:bg-white/5 hover:text-neutral-200"
            >
              <X size={16} />
            </button>
          )}
        </div>
        {/* Başlık ve alt çubuk yerinde durur, yalnızca içerik kayar: uzun formda
            düğmeler alanların üstüne binmez, her zaman görünür kalır. */}
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        {footer && (
          <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-neutral-900 px-4 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
